import React, { useEffect, useLayoutEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import type { NextPage } from 'next';

import { RadioGroup, FormControlLabel, Radio, Box } from '@mui/material';

import { BsCheckCircleFill } from 'react-icons/bs';
import { useQuery } from 'react-query';
import { scroller, Element } from 'react-scroll';
import { useRecoilValue } from 'recoil';

import { useMapData } from '@hooks_iqloud/useMapData';
import { useRefreshCompanyCache } from '@hooks_iqloud/useRefreshCompanyCache';

import { useCompanyData, CompanyTopTitles } from '@hooks_iqfu/dashboard/useCompanyData';
import { useCompanyMembershipAction } from '@hooks_iqfu/dashboard/useCompanyMembershipAction';
import { useGeneralAgency } from '@hooks_iqfu/dashboard/useGeneralAgency';
import { usePurchaseAgency } from '@hooks_iqfu/dashboard/usePurchaseAgency';
import { useReservationAvailability } from '@hooks_iqfu/dashboard/useReservationAvailability';

import { countAiAppraisalReportMonthlyUsage, formatAiAppraisalReportUsageCount } from '@utils_iqfu/aiAppraisalReport';
import { convertCompanyStrongPoint } from '@utils_iqfu/dashboard/convertCompanyStrongPoint';

import RightColumnAdminSalesRepresentative from '@components_iqfu/common/right_column/RightColumnAdminSalesRepresentative/RightColumnAdminSalesRepresentative';
import RightColumnCommissionRateTypeKey from '@components_iqfu/common/right_column/RightColumnCommissionRateTypeKey/RightColumnCommissionRateTypeKey';
import RightColumnDetailLinksInCompany from '@components_iqfu/common/right_column/RightColumnCompanyDetailLinks/RightColumnDetailLinksInCompany';
import RightColumnContactInfo from '@components_iqfu/common/right_column/RightColumnContactInfo/RightColumnContactInfo';
import RightColumnContainer from '@components_iqfu/common/right_column/RightColumnContainer/RightColumnContainer';
import RightColumnIsPublic from '@components_iqfu/common/right_column/RightColumnIsPublic/RightColumnIsPublic';
import RightColumnMemberShipStatus from '@components_iqfu/common/right_column/RightColumnMemberShipStatus/RightColumnMemberShipStatus';
import RightColumnMemo from '@components_iqfu/common/right_column/RightColumnMemo/RightColumnMemo';
import RightColumnSaleCases from '@components_iqfu/common/right_column/RightColumnSaleCases/RightColumnSaleCases';
import CheckBox from '@components_iqfu/dashboard/check_box/CheckBox';
import { CompanyProfile } from '@components_iqfu/dashboard/company_profile/CompanyProfile';
import DashboardConfirmDialog from '@components_iqfu/dashboard/dialog/confirm_dialog/DashboardConfirmDialog';
import DialogWrapper from '@components_iqfu/dashboard/dialog/dialog_wrapper/DialogWrapper';
import ForcePublishDialog from '@components_iqfu/dashboard/dialog/force_publish/ForcePublishDialog';
import CreateFreeMapDialog from '@components_iqfu/dashboard/dialog/free_map/CreateFreeMapDialog';
import CompanyPublicForAdminReason from '@components_iqfu/dashboard/dialog/helper_reason/CompanyPublicForAdminReason';
import LastMonthMembershipAnnouncementDialog from '@components_iqfu/dashboard/dialog/last_month_membership_announcement/LastMonthMembershipAnnouncementDialog';
import MaxmizeImagesDialogWrapper from '@components_iqfu/dashboard/dialog/maximize-image/MaxmizeImagesDialogWrapper';
import MembershipStatusChangeDialog from '@components_iqfu/dashboard/dialog/membership_status_change/MembershipStatusChangeDialog';
import HelperReason from '@components_iqfu/dashboard/helper_reason/HelperReason';
import InitialAutoMessage from '@components_iqfu/dashboard/initial_auto_message/InitialAutoMessage';
import InterviewArticles from '@components_iqfu/dashboard/interview_articles/InterviewArticles';
import SpecialFlagsView from '@components_iqfu/dashboard/special_flags/SpecialFlagsView';
import Stepper from '@components_iqfu/dashboard/stepper/Stepper';
import VisitReservationCalendar from '@components_iqfu/dashboard/visit_reservation_calendar/VisitReservationCalendar';

import { adminUserRoleSelector } from '@recoil_iqfu/atoms/auth/adminLoginUserData';

import { DisclosureStatementCompanyBuyerOptions } from '@constants_iqloud';

import {
  CompanyDetailType,
  CompanyMembershipActionButton,
  CompanyMembershipActionType,
  CompanyMembershipStatusToAvailableActionsMap,
  CompanyOverViewType,
  CompanyProfileDataType,
  CompanyProfileImageType,
  TagType,
} from '@type-def_iqfu/CompanyType';
import { SupportCitiesData, formattedSupportCities } from '@type-def_iqfu/public/SupportCitiesData';

import DashboardPageContainer from '@components_common/page_container/DashboardPageContainer';
import { CompanyMembershipStatus } from '@constants_common';
import {
  COMPANIES_SORT_TYPE_FOR_TOP_TITLES,
  CompanyProfileDataKeyList,
  CompanyStep,
  CompanyTypeCode,
  NotificationText,
  ProfileImageStatusType,
} from '@constants_iqfu';
import { useNotificationContext } from '@contexts_common/notificationContext';
import { useDialog } from '@hooks_common/useDialog';
import styles from '@styles_iqfu/dashboard/company.module.scss';

const Company: NextPage = () => {
  const {
    getCompanyDetail,
    updateStep,
    updateCompanyDetail,
    convertCompany,
    getCompanyOverView,
    getCompanyProfileData,
    updateDisclosureStatementCompanyBuyer,
    getCompanySupportCities,
    forcePublish,
    getCompanyTopTitles,
    sendRemindPublishEmail,
  } = useCompanyData();
  const { createFreeMap } = useMapData();
  const { refreshCompanyCache } = useRefreshCompanyCache();

  const { getPurchaseInfo } = usePurchaseAgency('admin');
  const { getGeneralAgencyInfo } = useGeneralAgency('admin');
  const { getReservationAvailability } = useReservationAvailability();

  const adminUserRole = useRecoilValue(adminUserRoleSelector);
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();
  const router = useRouter();
  const query = router.query;
  let company_id = String(router.query.company_id);

  const [profileData, setProfileData] = useState<CompanyProfileDataType>();
  const [companyData, setCompanyData] = useState<CompanyDetailType>();
  const [overView, setOverView] = useState<CompanyOverViewType[]>();
  const [fetchComplete, setFetchComplete] = useState(false);
  const [companyTags, setCompanyTags] = useState<TagType[]>();

  const [isPrepared, setIsPrepared] = useState({
    hearingSheet: false,
    profile: false,
    images: false,
  });
  const [isOpenForcePublishDialog, setIsOpenForcePublishDialog] = useState(false);

  const [isOpenMaxmizeImageDialog, setIsOpenMaxmizeImageDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [howToCompleteRegistrationDialogOpen, setHowToCompleteRegistrationDialogOpen] = useState(false);
  const [isOpenFreeMapDialog, setIsOpenFreeMapDialog] = useState(false);
  const [isOpenMembershipStatusChangeDialog, setIsOpenMembershipStatusChangeDialog] = useState(false);
  const [selectedMembershipAction, setSelectedMembershipAction] = useState<{
    action: CompanyMembershipActionType;
    label: string;
  } | null>(null);
  const lastMonthMembershipAnnouncementDialog = useDialog();
  const [disclosureStatementCompanyBuyer, setDisclosureStatementCompanyBuyer] =
    useState<keyof typeof DisclosureStatementCompanyBuyerOptions>('unconfirmed');
  const [purchaseAvailability, setPurchaseAvailability] = useState(false);
  const [supportCities, setSupportCities] = useState<formattedSupportCities>([]);
  const [topTitles, setTopTitles] = useState<CompanyTopTitles['company_top_titles']>([]);
  const isOpenConvertDialog = useDialog();

  const [readMoreButtonClicked, setReadMoreButtonClicked] = useState(false);

  const [updateFetch, setUpdateFetch] = useState(0);

  const { postCompanyMembershipStatusChangeAction } = useCompanyMembershipAction({ companyId: String(company_id) });

  const redirectToTop = () => {
    router.push(`/console/admin/dashboard`);
    setNotificationStatus('alert');
    setNotificationMessage(NotificationText.inaccessiblePage);
    setNotificationDisplay(true);
    return;
  };

  const { data: companyDetail } = useQuery(
    ['getCompanyDetail', company_id, updateFetch],
    () => getCompanyDetail(company_id),
    {
      onSuccess: (resonse) => {
        const { data, status } = resonse;
        if (status === 'success') {
          setCompanyData(data);
          setCompanyTags(data.company_tags);
          setPurchaseAvailability(data.purchase_availability);
          setDisclosureStatementCompanyBuyer(data.disclosure_statement_company_buyer);
          setPurchaseAvailability(data.purchase_availability);
        } else {
          redirectToTop();
        }
      },
      onError: (error) => {
        redirectToTop();
      },
      enabled: company_id !== 'undefined',
      staleTime: 600,
    },
  );

  const { data: companyOverview } = useQuery(
    ['getCompanyOverView', company_id, updateFetch],
    () => getCompanyOverView('admin', company_id),
    {
      onSuccess: (resonse) => {
        const { data, status } = resonse;
        if (status === 'success') {
          setOverView(data.overview_items);
        } else {
          redirectToTop();
        }
      },
      onError: (error) => {
        redirectToTop();
      },
      enabled: company_id !== 'undefined',
      staleTime: 600,
    },
  );

  const { data: companyProfile } = useQuery(
    ['getCompanyProfileData', company_id, updateFetch],
    () => getCompanyProfileData('admin', company_id),
    {
      onSuccess: (resonse) => {
        const { data, status } = resonse;
        if (status === 'success') {
          setProfileData(data);
        } else {
          redirectToTop();
        }
      },
      onError: (error) => {
        redirectToTop();
      },
      enabled: company_id !== 'undefined',
      staleTime: 600,
    },
  );

  const { data: companySupportCities } = useQuery(
    ['getCompanySupportCities', company_id, updateFetch],
    () => getCompanySupportCities('admin', company_id),
    {
      onSuccess: (resonse) => {
        const { data, status } = resonse;
        if (status === 'success') {
          const result = convertSupportCitiesData(data.support_cities);
          setSupportCities(result);
        } else {
          redirectToTop();
        }
      },
      onError: (error) => {
        redirectToTop();
      },
      enabled: company_id !== 'undefined',
      staleTime: 600,
    },
  );

  // The availability endpoint only resolves public companies that are still active, so
  // requesting it for a non-public, suspended or pre-discarded one always 404s
  const canFetchReservationAvailability =
    !!companyData?.company_services.includes('visit_reservation') &&
    companyData?.is_public === true &&
    companyData?.membership_status === 'active';

  const { data: reservationAvailability } = useQuery(
    ['getReservationAvailability', company_id, canFetchReservationAvailability],
    () => getReservationAvailability(Number(company_id)),
    {
      enabled: company_id !== 'undefined' && canFetchReservationAvailability,
    },
  );

  const convertSupportCitiesData = (data: SupportCitiesData[]): formattedSupportCities => {
    const result: formattedSupportCities = [];

    data.forEach((currentItem) => {
      const targetElement = result.find((item) => item.prefecture_code === currentItem.prefecture.code);

      if (!targetElement) {
        result.push({
          prefecture_code: currentItem.prefecture.code,
          prefecture_name: currentItem.prefecture.name,
          cities: [{ code: currentItem.city.code, name: currentItem.city.name }],
        });
      } else {
        const targetIndex = result.indexOf(targetElement);
        result[targetIndex].cities.push({
          code: currentItem.city.code,
          name: currentItem.city.name,
        });
      }
    });

    // resultをprefecture_codeで並び替える
    result.sort((a, b) => {
      if (a.prefecture_code < b.prefecture_code) {
        return -1;
      } else if (a.prefecture_code > b.prefecture_code) {
        return 1;
      } else {
        return 0;
      }
    });

    return result;
  };

  const { data: companyTopTitles } = useQuery(
    ['getCompanyTopTitles', company_id, updateFetch],
    () => getCompanyTopTitles(company_id),
    {
      onSuccess: (resonse) => {
        const { data, status } = resonse;
        if (status === 'success') {
          setTopTitles(data.company_top_titles);
        } else {
          redirectToTop();
        }
      },
      onError: (error) => {
        redirectToTop();
      },
      enabled: company_id !== 'undefined',
      staleTime: 600,
    },
  );

  const { data: purchaseAgencyInfo } = useQuery(
    ['getPurchaseInfo', company_id, companyData],
    () => getPurchaseInfo(company_id),
    {
      enabled: company_id !== 'undefined' && companyData?.company_type === 'purchase_agency',
    },
  );

  const isGeneralAgency = !!companyData && companyData.company_type !== CompanyTypeCode.purchaseAgency;
  const { data: generalAgencyInfo } = useQuery(
    ['getGeneralAgencyInfo', company_id],
    () => getGeneralAgencyInfo(company_id),
    { enabled: company_id !== 'undefined' && isGeneralAgency },
  );

  useEffect(() => {
    (async () => {
      if (companyDetail && companyOverview && companyProfile && companySupportCities && companyTopTitles) {
        //買取専門会社の場合は買取情報も取得する必要がある
        if (companyDetail.data.company_type === 'purchase_agency') {
          purchaseAgencyInfo && setFetchComplete(true);
        } else {
          setFetchComplete(true);
        }
      }
    })();
  }, [companyDetail, companyOverview, companyProfile, companySupportCities, companyTopTitles, purchaseAgencyInfo]);

  useLayoutEffect(() => {
    // 会社情報のfetchが完了したかどうか
    if (fetchComplete && query.position) {
      scroller.scrollTo(String(query.position), {
        duration: 0,
        containerId: 'dashboard_main_container',
      });
    }
  }, [fetchComplete, query.position]);

  const updateStepAction = async (step: keyof typeof CompanyStep) => {
    const result = await updateStep(String(company_id), step);
    if (result?.success) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successUpdateStep);
      setNotificationDisplay(true);
      setUpdateFetch(updateFetch + 1);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
      setNotificationDisplay(true);
    }
  };

  const handleIsPublic = async () => {
    if (companyData) {
      //update用に不要なpropertyは分割
      const {
        town,
        city,
        prefecture,
        full_address,
        step,
        free_map_code,
        company_tags,
        chat_inquiry_room_id,
        iqfu,
        cloud,
        destroyable,
        disclosure_statement_company_buyer,
        ...extractCompanyData
      } = companyData;
      const requestPayload = {
        ...extractCompanyData,
        town_code: town.code,
        is_public: !companyData.is_public,
      };
      const result = await updateCompanyDetail(String(company_id), requestPayload);
      if (result?.success) {
        setNotificationStatus('success');
        setNotificationMessage(NotificationText.successUpdateHandlePublic);
        setNotificationDisplay(true);
        setUpdateFetch(updateFetch + 1);
      } else {
        setNotificationStatus('alert');
        setNotificationMessage(NotificationText.error);
        setNotificationDisplay(true);
      }
    }
  };

  const handleForcePublishDialog = () => {
    setIsOpenForcePublishDialog(!isOpenForcePublishDialog);
  };

  const handleFreeMapDialog = () => {
    setIsOpenFreeMapDialog(!isOpenFreeMapDialog);
  };

  const handleFreeMapDialogClose = () => {
    setIsOpenFreeMapDialog(false);
  };

  const handleConvert = async () => {
    const result = await convertCompany(company_id);

    if (result.success === true) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successConvertCompany);
      setNotificationDisplay(true);
      setUpdateFetch(updateFetch + 1);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
      setNotificationDisplay(true);
    }
    isOpenConvertDialog.close();
  };

  const handleMembershipStatusChangeDialogOpen = (action: CompanyMembershipActionType, label: string) => {
    setSelectedMembershipAction({ action, label });
    setIsOpenMembershipStatusChangeDialog(true);
  };

  const handleMembershipStatusChangeDialogClose = () => {
    setIsOpenMembershipStatusChangeDialog(false);
    setSelectedMembershipAction(null);
  };

  const confirmMembershipStatusChange = async () => {
    if (selectedMembershipAction) {
      const result = await postCompanyMembershipStatusChangeAction(selectedMembershipAction.action);

      if (result.success === true) {
        setNotificationStatus('success');
        setNotificationMessage(NotificationText.successUpdateMembershipStatus);
        setNotificationDisplay(true);
        setUpdateFetch(updateFetch + 1);
        if (selectedMembershipAction.action === 'discard') {
          router.push('/console/admin/company');
        }
      } else {
        setNotificationStatus('alert');
        setNotificationMessage(NotificationText.error);
        setNotificationDisplay(true);
      }
      handleMembershipStatusChangeDialogClose();
    }
  };

  const handleLastMonthMembershipAnnouncement = async (selectedItems: string[]) => {
    const { status } = await sendRemindPublishEmail(company_id, selectedItems);
    setNotificationDisplay(true);
    if (status === 'success') {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successLastMonthMembershipAnnouncement);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
    }
    lastMonthMembershipAnnouncementDialog.close();
  };

  const createNewFreeMap = async () => {
    const { status } = await createFreeMap(String(company_id));
    if (status === 'success') {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successCreateFreeMap);
      setNotificationDisplay(true);
      handleFreeMapDialogClose();
      setUpdateFetch(updateFetch + 1);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
      setNotificationDisplay(true);
    }
  };

  const updateDisclosureStatementSetting = async () => {
    const { result, status } = await updateDisclosureStatementCompanyBuyer(
      String(company_id),
      disclosureStatementCompanyBuyer,
    );
    if (status === 'success') {
      setNotificationStatus('success');
      setNotificationMessage('買主が企業だった場合の扱いを更新しました');
      setNotificationDisplay(true);
      setUpdateFetch(updateFetch + 1);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
      setNotificationDisplay(true);
    }
  };

  const forcePublishAction = async () => {
    const result = await forcePublish(String(company_id));
    if (result?.success) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successForcePublish);
      setNotificationDisplay(true);
      router.reload();
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.error);
      setNotificationDisplay(true);
    }
  };

  const outlineStyle = (status: keyof typeof ProfileImageStatusType) => {
    if (status === 'unapproved') {
      return styles.unapproved_image_outline;
    } else if (status === 'rejected') {
      return styles.rejected_image_outline;
    }
  };

  const handleCloseMaxmizeImageDialog = () => {
    setIsOpenMaxmizeImageDialog(false);
  };

  const refreshCashHandler = async (company_id: string) => {
    const { success } = await refreshCompanyCache(company_id);
    if (success) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successRefreshCash);
      setNotificationDisplay(true);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(NotificationText.failedRefreshCash);
      setNotificationDisplay(true);
    }
  };

  const isMasterOrManager = adminUserRole === 'master' || adminUserRole === 'manager';
  const canEditVisitReservationSetting =
    adminUserRole === 'master' || adminUserRole === 'manager' || adminUserRole === 'member';

  const shouldDisplayLastMonthAnnouncement =
    companyData && companyData?.step !== 'completed' && companyData?.company_type === 'general_agency';

  const rightColumn = (
    <RightColumnContainer noHeader>
      <RightColumnDetailLinksInCompany
        roomId={String(companyDetail?.data.chat_inquiry_room_id)}
        company={{ id: String(company_id), name: companyDetail?.data.name }}
      />
      {companyData && <RightColumnIsPublic isPublic={companyData?.is_public} />}
      {companyData && <RightColumnMemberShipStatus companyData={companyData} />}
      {companyData && <RightColumnSaleCases companyData={companyData} />}
      {companyData && <RightColumnCommissionRateTypeKey companyData={companyData} />}
      <RightColumnContactInfo targetId={company_id} />
      <RightColumnAdminSalesRepresentative targetId={String(company_id)} />
      <RightColumnMemo operationalNoteType={'company'} targetId={String(company_id)} />
      <RightColumnMemo operationalNoteType={'sales'} targetId={String(company_id)} />
    </RightColumnContainer>
  );

  return (
    <DashboardPageContainer userType="admin" rightColumn={rightColumn}>
      <Element name="test"></Element>
      {fetchComplete && (
        <>
          <div className={`${styles.section_title_wrap} ${styles.sticky}`}>
            <div className={styles.flex}>
              <h3 className={`${styles.section_title} ${styles.company_name}`}>{companyData?.name}</h3>
              <div className={styles.is_public_label}>{companyData?.is_public ? '公開' : '非公開'}</div>
            </div>
            <div className={styles.header_button_flex}>
              {isMasterOrManager && !companyData?.is_public && (
                <button
                  className={`${styles.link_button} ${styles.delete}`}
                  type="submit"
                  onClick={handleForcePublishDialog}
                >
                  会社ページ運営公開
                </button>
              )}
              <Link href={`${process.env.NEXT_PUBLIC_CLIENT_BASEPATH}/companies/${company_id}`} passHref>
                <a
                  target="_blank"
                  className={`${styles.link_button} ${styles.page_preview} ${
                    !companyData?.is_public && styles.inactive
                  }`}
                >
                  ページ確認
                </a>
              </Link>
              <button
                onClick={() => refreshCashHandler(company_id)}
                className={`${styles.link_button} ${styles.page_preview} ${!companyData?.is_public && styles.inactive}`}
              >
                キャッシュ更新
              </button>
              <Link href={`/console/admin/company/${company_id}/user`} passHref>
                <a className={styles.link_button}>ユーザー</a>
              </Link>
              <Link href={`/console/admin/company/${company_id}/company_notification_emails`} passHref>
                <a className={styles.link_button}>通知先</a>
              </Link>
              <Link href={`/console/admin/company/${company_id}/information`} passHref>
                <a className={styles.link_button}>基本情報</a>
              </Link>
              <Link href={`/console/admin/company/${company_id}/reciprocal_link`} passHref>
                <a className={styles.link_button}>相互リンク</a>
              </Link>
            </div>
          </div>

          {companyData?.step && companyData.step !== 'completed' && (
            <div className={styles.stepper_wrapper}>
              <label className={styles.stepper_label}>登録状況</label>
              <div>
                <Stepper step={companyData.step} />
                <HelperReason
                  question_text="登録を完了するには ?"
                  handleModalOpen={() => setHowToCompleteRegistrationDialogOpen(true)}
                />
              </div>
            </div>
          )}
          {(shouldDisplayLastMonthAnnouncement || companyData?.step === 'created') && (
            <div className={styles.registration_actions_wrapper}>
              <Box sx={{ width: '240px', margin: '8px 24px 8px 0' }}>
                <button
                  className={`${styles.last_month_membership_announcement_button} ${
                    !shouldDisplayLastMonthAnnouncement ? styles.hidden : ''
                  }`}
                  onClick={lastMonthMembershipAnnouncementDialog.open}
                >
                  先月加盟公開催促メール
                </button>
              </Box>
              {companyData?.step === 'created' && (
                <Box
                  sx={{
                    width: shouldDisplayLastMonthAnnouncement ? 'calc(100% - 240px)' : '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CheckBox
                    checked={isPrepared.hearingSheet}
                    onClick={() => {
                      setIsPrepared({
                        ...isPrepared,
                        hearingSheet: !isPrepared.hearingSheet,
                      });
                    }}
                    label="不動産会社からヒアリングシートを受領しました"
                  />
                  <button
                    className={`${styles.step_button} ${!isPrepared.hearingSheet && styles.inactive}`}
                    onClick={() => updateStepAction(companyData.step)}
                    disabled={!isPrepared.hearingSheet}
                  >
                    次のステップへ進む
                  </button>
                </Box>
              )}
            </div>
          )}

          {companyData?.step === 'preparing' && (
            <div className={`${styles.input_item} ${styles.step_confirm}`}>
              <label className={styles.input_item_label} />
              <div className={styles.grid}>
                <CheckBox
                  checked={isPrepared.profile}
                  onClick={() => {
                    setIsPrepared({
                      ...isPrepared,
                      profile: !isPrepared.profile,
                    });
                  }}
                  label="ライターが紹介文を作成しました"
                />
                <CheckBox
                  checked={isPrepared.images}
                  onClick={() => {
                    setIsPrepared({
                      ...isPrepared,
                      images: !isPrepared.images,
                    });
                  }}
                  label="写真の準備を依頼しました"
                />
              </div>
              <button
                className={`${styles.step_button} ${(!isPrepared.profile || !isPrepared.images) && styles.inactive}`}
                onClick={() => updateStepAction(companyData?.step)}
                disabled={!isPrepared.profile || !isPrepared.images}
              >
                次のステップへ進む
              </button>
            </div>
          )}

          {companyData?.step === 'completed' && (
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>会社タイプ</label>
              {companyData?.company_type === 'general_agency' ? '仲介' : '買取'}
              {adminUserRole === 'master' && (
                <button className={styles.service_item_link_button} onClick={() => isOpenConvertDialog.open()}>
                  コンバート
                </button>
              )}
            </div>
          )}

          {companyData?.step === 'completed' && (
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>公開状態</label>
              {companyData?.is_public ? '公開中' : '非公開中'}
              <button className={styles.service_item_link_button} onClick={handleIsPublic}>
                {companyData?.is_public ? '非公開にする' : '公開する'}
              </button>
            </div>
          )}

          {/* 加盟状態セクション */}
          <div className={styles.input_item}>
            <label className={styles.input_item_label}>加盟状態</label>
            {companyData?.membership_status && CompanyMembershipStatus[companyData?.membership_status]}
            {isMasterOrManager && companyData?.membership_status && (
              <div className={styles.membership_status_available_buttons}>
                {CompanyMembershipStatusToAvailableActionsMap[companyData.membership_status].map(
                  (availableAction: CompanyMembershipActionButton, i: number) => (
                    <button
                      key={`${companyData.membership_status}-${i}`}
                      className={`${styles.membership_status_available_button} ${styles.delete}`}
                      onClick={() =>
                        handleMembershipStatusChangeDialogOpen(availableAction.action, availableAction.label)
                      }
                    >
                      {availableAction.label}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>

          {isMasterOrManager && (
            <div>
              <Link href={`/console/admin/company/${company_id}/service`} passHref>
                <a className={styles.service_setting_link}>加盟日設定</a>
              </Link>
            </div>
          )}

          {/* イクラウド加盟済の会社に無料マップは発行しない */}
          {!companyData?.cloud.start_date && (
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>無料マップURL</label>
              {companyData?.free_map_code
                ? process.env.NEXT_PUBLIC_IQLOUD_EMBED_MAP_URL + '/embed/' + companyData?.free_map_code
                : '未発行'}
              {!companyData?.free_map_code && (
                <button className={styles.service_item_link_button} onClick={handleFreeMapDialog}>
                  発行する
                </button>
              )}
            </div>
          )}

          {companyData?.cloud.start_date && (
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>買主が企業だった場合の扱い</label>
              <RadioGroup
                row
                className={styles.radio}
                value={disclosureStatementCompanyBuyer}
                onChange={(event) => {
                  setDisclosureStatementCompanyBuyer(
                    event.target.value as keyof typeof DisclosureStatementCompanyBuyerOptions,
                  );
                }}
              >
                <FormControlLabel
                  value="unconfirmed"
                  control={<Radio />}
                  label={DisclosureStatementCompanyBuyerOptions['unconfirmed']}
                />
                <FormControlLabel
                  value="confirming"
                  control={<Radio />}
                  label={DisclosureStatementCompanyBuyerOptions['confirming']}
                />
                <FormControlLabel
                  value="all_mediation"
                  control={<Radio />}
                  label={DisclosureStatementCompanyBuyerOptions['all_mediation']}
                />
                <FormControlLabel
                  value="all_purchase"
                  control={<Radio />}
                  label={DisclosureStatementCompanyBuyerOptions['all_purchase']}
                />
                <FormControlLabel
                  value="company_selected_deal_type"
                  control={<Radio />}
                  label={DisclosureStatementCompanyBuyerOptions['company_selected_deal_type']}
                />
              </RadioGroup>
              <button className={styles.link_button} onClick={updateDisclosureStatementSetting}>
                更新
              </button>
            </div>
          )}

          <div className={styles.input_item}>
            <table className={styles.sale_case_table} cellSpacing="0">
              <thead className={styles.table_head}>
                <tr>
                  <th>イクラウド</th>
                  <th>買取専門</th>
                  <th>PRO</th>
                  <th>リースバック</th>
                  <th>埋め込み地図</th>
                  <th>売却LP</th>
                  <th>AI査定</th>
                  <th>来店予約</th>
                  <th>口コミ連動</th>
                </tr>
              </thead>
              <tbody className={styles.table_body}>
                <tr>
                  <td>{companyData?.cloud.has_cloud ? '⚪' : '×'}</td>
                  <td>{companyData?.company_type === 'purchase_agency' ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('pro') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('leaseback') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('embed_map') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('selling_page') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('ai_appraisal') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('visit_reservation') ? '⚪' : '×'}</td>
                  <td>{companyData?.company_services.includes('user_voice') ? '⚪' : '×'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {companyData?.company_services.includes('ai_appraisal') && companyData.ai_appraisal_report_usage && (
            <div className={styles.input_item}>
              <label className={styles.input_item_label}>AI査定書 今月の使用回数</label>
              {formatAiAppraisalReportUsageCount(
                countAiAppraisalReportMonthlyUsage(companyData.ai_appraisal_report_usage),
                companyData.ai_appraisal_report_usage.limit,
              )}
              （リセット: {companyData.ai_appraisal_report_usage.resets_on}）
            </div>
          )}

          <div className={styles.input_item}>
            <table className={styles.sale_case_table} cellSpacing="0">
              <thead className={styles.table_head}>
                <tr>
                  <th></th>
                  <th>累積実績登録数</th>
                  <th>直近３ヶ月の登録数</th>
                </tr>
              </thead>
              <tbody className={styles.table_body}>
                <tr>
                  <td>イクラ不動産</td>
                  <td>{companyData?.iqfu.total_count}</td>
                  <td>{companyData?.iqfu.recent_count}</td>
                </tr>
                {companyData?.cloud.start_date && (
                  <tr>
                    <td>イクラウド</td>
                    <td>{companyData?.cloud.total_count}</td>
                    <td>{companyData?.cloud.recent_count}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className={styles.company_detail_wrapper}>
            <div className={styles.detail_area_section}>
              <div className={styles.section_title_wrap}>
                <h3 className={styles.contents_title}>会社紹介写真</h3>
              </div>

              <div className={styles.image_wrapper}>
                {profileData?.files &&
                  profileData?.files.map((file: CompanyProfileImageType) => {
                    return file.is_main ? (
                      <div className={styles.image_frame} key={file.id}>
                        <div className={styles.main_image}>
                          <img
                            src={file.file}
                            alt="紹介写真"
                            className={styles.profile_image}
                            onClick={() => {
                              setPreviewImage(file.file);
                              setIsOpenMaxmizeImageDialog(true);
                            }}
                          />
                          <div className={styles.main_label}>メイン写真</div>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.image_frame} key={file.id}>
                        <div className={outlineStyle(file.status)}>
                          <img
                            src={file.file}
                            alt="紹介写真"
                            className={styles.profile_image}
                            onClick={() => {
                              setPreviewImage(file.file);
                              setIsOpenMaxmizeImageDialog(true);
                            }}
                          />
                        </div>
                        {file.status === 'unapproved' && <div className={styles.unapproved_label}>承認待ち</div>}
                        {file.status === 'rejected' && <div className={styles.rejected_label}>差し戻し済</div>}
                      </div>
                    );
                  })}
              </div>
              {profileData?.files && profileData?.files.length === 0 && (
                <div className={styles.unregistered_text}>会社紹介写真が未登録です</div>
              )}

              <Link href={`/console/admin/company/${company_id}/profile_images`} passHref>
                <a className={`${styles.edit_button} ${styles.large}`}>写真編集</a>
              </Link>
            </div>

            <div className={styles.detail_area_section}>
              <Element name="profile">
                <div className={styles.section_title_wrap}>
                  <h3 className={styles.contents_title}>会社紹介情報</h3>
                </div>
                <div className={styles.input_item}>
                  <label className={styles.input_item_label}>アピールポイント</label>
                  {profileData?.top_title}
                </div>
                <div className={styles.input_item}>
                  <label className={styles.input_item_label}>AI要約</label>
                  {profileData?.ai_profile_summary}
                </div>
                {CompanyProfileDataKeyList.map(({ title, body }, index) => {
                  if (index > 3 && !readMoreButtonClicked) return <></>;
                  return (
                    <CompanyProfile
                      key={index.toString + title + body}
                      index={index + 1}
                      title={profileData && profileData[title]}
                      body={profileData && profileData[body]}
                      inputItem={styles.input_item}
                      inputItemLabel={styles.input_item_label}
                    />
                  );
                })}
                {!readMoreButtonClicked && (
                  <button
                    className={`${styles.read_more_button}`}
                    onClick={() => {
                      setReadMoreButtonClicked(true);
                    }}
                  >
                    もっと見る
                  </button>
                )}
              </Element>

              <Link href={`/console/admin/company/${company_id}/profile`} passHref>
                <a className={`${styles.edit_button} ${styles.large}`}>紹介情報編集</a>
              </Link>
            </div>

            {companyData?.company_type !== 'purchase_agency' && (
              <div className={styles.detail_area_section}>
                <Element name="strong_point">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>強み設定</h3>
                  </div>
                  <div className={styles.input_item}>
                    <label className={styles.input_item_label}>強み</label>
                    {convertCompanyStrongPoint(String(companyData?.strong_point))}
                  </div>
                </Element>

                <Link href={`/console/admin/company/${company_id}/strong_point`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>強み編集</a>
                </Link>
              </div>
            )}

            {companyData?.company_type !== 'purchase_agency' && (
              <div className={styles.detail_area_section}>
                <Element name="tags">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>タグ設定</h3>
                  </div>
                  {companyTags && (
                    <div className={styles.tag_inputs}>
                      <div className={styles.profile_input_item}>
                        {companyTags.map((tag) => {
                          return (
                            <div className={styles.tag_texts} key={tag.id}>
                              <div className={styles.tag_flex}>
                                <BsCheckCircleFill />
                                {tag.name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Element>

                <Link href={`/console/admin/company/${company_id}/tags`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>タグ編集</a>
                </Link>
              </div>
            )}

            {companyData?.company_type !== 'purchase_agency' && (
              <div className={styles.detail_area_section}>
                <Element name="purchase_availability">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>買取対応</h3>
                  </div>
                  <div className={styles.input_item}>{purchaseAvailability ? '対応可' : '対応不可'}</div>
                </Element>

                <Link href={`/console/admin/company/${company_id}/purchase_availability`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>買取対応編集</a>
                </Link>
              </div>
            )}

            {companyData?.company_type === 'purchase_agency' && purchaseAgencyInfo && (
              <div className={styles.detail_area_section}>
                <Element name="purchase_info">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>買取情報</h3>
                  </div>
                </Element>
                <div className={styles.input_item}>
                  <label className={styles.input_item_label}>築年数の上限制限</label>
                  {purchaseAgencyInfo.max_building_age ? `${purchaseAgencyInfo.max_building_age}年` : 'なし'}
                </div>
                <div className={styles.input_item}>
                  <label className={styles.input_item_label}>買取金額の上限</label>
                  {purchaseAgencyInfo.max_price ? `${purchaseAgencyInfo.max_price}万円` : 'なし'}
                </div>
                <div className={styles.input_item}>
                  <label className={styles.input_item_label}>買取案件の紹介の可否</label>
                  {purchaseAgencyInfo.b2b_availability ? '可能' : '不可'}
                </div>
                <SpecialFlagsView
                  propertyTypes={purchaseAgencyInfo.support_property_types}
                  characteristics={purchaseAgencyInfo.support_property_characteristics}
                  propertyTypeLabel="買取対応している物件種別"
                />

                <Link href={`/console/admin/company/${company_id}/purchase_info`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>買取情報編集</a>
                </Link>
              </div>
            )}

            {topTitles && topTitles.length > 0 && (
              <div className={styles.detail_area_section}>
                <Element name="top_titles">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>検索軸別アピール文</h3>
                  </div>
                </Element>
                {topTitles.map((data, i) => {
                  return (
                    <div className={styles.input_item} key={i}>
                      <label className={styles.input_item_label}>
                        {COMPANIES_SORT_TYPE_FOR_TOP_TITLES[data.sort_type]}
                      </label>
                      {data.content}
                    </div>
                  );
                })}
                <Link href={`/console/admin/company/${company_id}/top_titles`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>検索軸別アピール文編集</a>
                </Link>
              </div>
            )}

            {companyData?.company_type !== CompanyTypeCode.purchaseAgency && (
              <div className={styles.detail_area_section}>
                <Element name="special_flags">
                  <div className={styles.section_title_wrap}>
                    <h3 className={styles.contents_title}>特殊フラグ</h3>
                  </div>
                </Element>
                <SpecialFlagsView
                  propertyTypes={generalAgencyInfo?.support_property_types}
                  characteristics={generalAgencyInfo?.support_property_characteristics}
                  propertyTypeLabel="物件タイプ"
                />

                <Link href={`/console/admin/company/${company_id}/special_flags`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>特殊フラグ編集</a>
                </Link>
              </div>
            )}

            <div className={styles.detail_area_section}>
              <Element name="support_cities">
                <div className={styles.title_wrap}>
                  <h3 className={styles.contents_title}>売却・買取対応エリア</h3>
                </div>
                <div className={styles.support_area_container}>
                  {supportCities &&
                    supportCities.length > 0 &&
                    supportCities.map((prefecture, i) => {
                      return (
                        <div key={i} className={styles.support_area_prefecture_block}>
                          <p className={styles.support_area_prefecture_name}>{prefecture.prefecture_name}</p>
                          <ul className={styles.support_cities_stacks}>
                            {prefecture.cities.map((city, i) => {
                              return <li key={i}>{city.name}</li>;
                            })}
                          </ul>
                        </div>
                      );
                    })}
                </div>
              </Element>

              <Link href={`/console/admin/company/${company_id}/support_cities`} passHref>
                <a className={`${styles.edit_button} ${styles.large}`}>対応エリア編集</a>
              </Link>
            </div>

            <div className={styles.detail_area_section}>
              <Element name="overview">
                <div className={styles.section_title_wrap}>
                  <h3 className={styles.contents_title}>概要情報</h3>
                </div>
                {overView?.map((data: CompanyOverViewType) => {
                  return (
                    <div className={styles.input_item} key={data.id}>
                      <label className={styles.input_item_label}>{data.name}</label>
                      {data.content}
                    </div>
                  );
                })}
              </Element>

              <Link href={`/console/admin/company/${company_id}/overview`} passHref>
                <a className={`${styles.edit_button} ${styles.large}`}>概要情報編集</a>
              </Link>
            </div>

            {/* Operators need the calendar even for a company whose availability cannot be fetched
                (service not joined, non-public, request failed), so they can set it up on request */}
            <div className={styles.detail_area_section}>
              <Element name="visit_reservation">
                <div className={styles.section_title_wrap}>
                  <h3 className={styles.contents_title}>来店予約・訪問査定予約・オンライン訪問査定予約</h3>
                </div>
                <VisitReservationCalendar dates={reservationAvailability?.dates ?? []} />
              </Element>

              {canEditVisitReservationSetting && (
                <Link href={`/console/admin/company/${company_id}/visit_reservation_settings`} passHref>
                  <a className={`${styles.edit_button} ${styles.large}`}>予約日程編集</a>
                </Link>
              )}
            </div>

            <InterviewArticles companyId={company_id} />
            <InitialAutoMessage companyId={company_id} />

            <MaxmizeImagesDialogWrapper
              open={isOpenMaxmizeImageDialog}
              onClose={() => handleCloseMaxmizeImageDialog()}
              previewImage={previewImage}
            />

            {howToCompleteRegistrationDialogOpen && (
              <DialogWrapper
                open={howToCompleteRegistrationDialogOpen}
                onClose={() => setHowToCompleteRegistrationDialogOpen(false)}
              >
                <CompanyPublicForAdminReason />
              </DialogWrapper>
            )}

            <DialogWrapper open={isOpenConvertDialog.isOpen} onClose={isOpenConvertDialog.close}>
              <DashboardConfirmDialog
                onClick={handleConvert}
                confirmMessage="会社タイプを変更しますか？「コンバート」を実行します"
                buttonText="実行"
              />
            </DialogWrapper>
            {isOpenForcePublishDialog && (
              <DialogWrapper open={isOpenForcePublishDialog} onClose={() => setIsOpenForcePublishDialog(false)}>
                <ForcePublishDialog handleSubmit={forcePublishAction} />
              </DialogWrapper>
            )}

            <DialogWrapper open={isOpenFreeMapDialog} onClose={handleFreeMapDialogClose}>
              <CreateFreeMapDialog onSubmit={() => createNewFreeMap()} companyName={String(companyData?.name)} />
            </DialogWrapper>

            {selectedMembershipAction && (
              <DialogWrapper
                open={isOpenMembershipStatusChangeDialog}
                onClose={handleMembershipStatusChangeDialogClose}
              >
                <MembershipStatusChangeDialog
                  confirmAction={confirmMembershipStatusChange}
                  actionLabel={selectedMembershipAction.label}
                />
              </DialogWrapper>
            )}

            <DialogWrapper
              open={lastMonthMembershipAnnouncementDialog.isOpen}
              onClose={lastMonthMembershipAnnouncementDialog.close}
            >
              <LastMonthMembershipAnnouncementDialog onConfirm={handleLastMonthMembershipAnnouncement} />
            </DialogWrapper>
          </div>
        </>
      )}
    </DashboardPageContainer>
  );
};

export default Company;
